import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const SCREENSHOTS = [
  '/PT Dashboard.png',
  '/PT Cycles.png',
  '/PT Compounds.png',
  '/PT Calculator.png',
  '/PT Admin Log.png',
  '/PT Recon.png',
];
const C = {
  bg: '#070B10',
  surface: '#0D1520',
  border: '#1A2A40',
  accent: '#00D4FF',
  text: '#E8F0F8',
  muted: '#5A7A99',
  yellow: '#FFD166',
  red: '#FF4D6A',
  green: '#00E5A0',
  mono: "'JetBrains Mono','Fira Mono',monospace",
  sans: "'Inter',system-ui,sans-serif",
};

export default function AuthScreen({ isRecovery = false, onRecoveryDone }) {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [currentScreenshot, setCurrentScreenshot] = useState(0);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScreenshot(prev => (prev + 1) % SCREENSHOTS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Surface a clear message if a password-reset link is expired/invalid.
  // (Recovery-session detection itself lives in AuthContext, passed in as `isRecovery`,
  // so there's only ever one listener for the PASSWORD_RECOVERY auth event.)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.substring(1));
      const description = params.get('error_description');
      setError(
        description
          ? description.replace(/\+/g, ' ')
          : 'This link is invalid or has expired. Please request a new one.'
      );
      window.history.replaceState(null, '', window.location.pathname);
      setMode('reset');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setBusy(true);
    try {
      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
       setMessage('Check your email for a confirmation link! If you don\'t see it within a minute, check your junk or spam folder.');
        setEmail('');
        setPassword('');
      } else if (mode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/`,
        });
        if (error) throw error;
    setMessage('Check your email for password reset instructions. If you don\'t see it, check your junk or spam folder.');
        setEmail('');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setBusy(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setMessage('Password updated!');
      setNewPassword('');
      setTimeout(() => {
        onRecoveryDone && onRecoveryDone();
      }, 1200);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setBusy(false);
    }
  };

  const labelStyle = { display: 'block', marginBottom: 6, fontSize: 12, fontFamily: C.mono, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase' };
  const inputStyle = { width: '100%', padding: '10px 12px', marginTop: 6, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: C.sans, fontSize: 14, boxSizing: 'border-box' };
  const linkStyle = { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', textDecoration: 'underline', fontFamily: C.mono, fontSize: 12 };
  const buttonStyle = { width: '100%', padding: 12, marginTop: 8, background: C.accent, color: C.bg, border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700, fontFamily: C.sans, cursor: 'pointer' };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: C.sans, fontSize: window.innerWidth < 768 ? 14 : 16, overflow: 'hidden' }}>
      {SCREENSHOTS.map((src, i) => (
        <div
          key={i}
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url("${src}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            opacity: i === currentScreenshot ? 1 : 0,
            transition: 'opacity 1.5s ease-in-out',
            filter: 'blur(1px)',
          }}
        />
      ))}
      <div style={{ position: 'absolute', inset: 0, background: C.bg, opacity: 0 }} />

      <div style={{ position: 'relative', width: '100%', maxWidth: 360, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, justifyContent: 'center' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.accent }} />
          <span style={{ fontFamily: C.mono, fontSize: 14, letterSpacing: '0.12em', color: C.text, fontWeight: 700 }}>PROTOCOL</span>
          <span style={{ fontFamily: C.mono, fontSize: 11, color: C.text, letterSpacing: '0.08em' }}>tracker</span>
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
          {isRecovery ? (
            <form onSubmit={handleSetNewPassword}>
              <p style={{ fontSize: 13, fontFamily: C.sans, color: C.text, marginTop: 0, marginBottom: 16 }}>
                Choose a new password for your account.
              </p>
              <label style={{ display: 'block', marginBottom: 16 }}>
                <span style={labelStyle}>New password</span>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} style={inputStyle} />
              </label>
              {error && <p style={{ color: C.red, fontSize: 13, fontFamily: C.mono }}>{error}</p>}
              {message && <p style={{ color: C.green, fontSize: 13, fontFamily: C.mono }}>{message}</p>}
              <button type="submit" disabled={busy} style={{ ...buttonStyle, opacity: busy ? 0.7 : 1 }}>
                {busy ? 'Please wait...' : 'Set new password'}
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit}>
                <label style={{ display: 'block', marginBottom: 16 }}>
                  <span style={labelStyle}>Email</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
                </label>
                {mode !== 'reset' && (
                  <label style={{ display: 'block', marginBottom: 16 }}>
                    <span style={labelStyle}>Password</span>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} style={inputStyle} />
                  </label>
                )}
                {error && <p style={{ color: C.red, fontSize: 13, fontFamily: C.mono }}>{error}</p>}
                {message && <p style={{ color: C.green, fontSize: 13, fontFamily: C.mono }}>{message}</p>}
                <button type="submit" disabled={busy} style={{ ...buttonStyle, opacity: busy ? 0.7 : 1 }}>
                  {busy ? 'Please wait...' : mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Sign up' : 'Send reset email'}
                </button>
              </form>
              <div style={{ marginTop: 18, fontSize: 12, fontFamily: C.mono, color: C.muted, textAlign: 'center' }}>
                {mode === 'signin' && (
                  <>
                    <button onClick={() => setMode('signup')} style={linkStyle}>Create an account</button>
                    {' · '}
                    <button onClick={() => setMode('reset')} style={linkStyle}>Forgot password?</button>
                  </>
                )}
                {mode === 'signup' && <button onClick={() => setMode('signin')} style={linkStyle}>Already have an account? Sign in</button>}
                {mode === 'reset' && <button onClick={() => setMode('signin')} style={linkStyle}>Back to sign in</button>}
              </div>
              {mode === 'signup' && (
                <div style={{ marginTop: 24, padding: 14, background: C.surface, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 11, fontFamily: C.mono, color: C.muted, textAlign: 'center' }}>
                  Sign up and get <span style={{ color: C.yellow }}>14 days free</span> to explore. No credit card required.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}